<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockReturn.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockReturn.pageTitle'),
            href: '/admin/stock-return'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-9">
              <ProductItemDetail
                v-model="model"
                @loading="value => (loading = value)"
              />
            </div>
            <div class="col-md-3">
              <div
                class="ts-bg-gray-200 sm:ts-border-blue-500 ts-rounded-md ts-p-3 ts-space-y-3"
              >
                <div>
                  <label class="required">{{
                    $t('stockReturn.stockReturnDate')
                  }}</label>

                  <a-date-picker
                    v-model="model.return_date"
                    format="DD-MM-YYYY"
                    valueFormat="DD-MM-YYYY"
                    class="ts-w-full"
                  ></a-date-picker>
                  <div class="validate" v-if="errors.has('return_date')">
                    {{ errors.first('return_date') }}
                  </div>
                </div>
                <div>
                  <label class="required">{{
                    $t('stockReturn.customer')
                  }}</label>
                  <a-select
                    v-model="model.customer_id"
                    class="ts-w-full"
                    :options="customers"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                  ></a-select>
                  <div class="validate" v-if="errors.has('customer_id')">
                    {{ errors.first('customer_id') }}
                  </div>
                </div>
                <div>
                  <label class="required">{{
                    $t('stockReturn.returnBy')
                  }}</label>
                  <a-select
                    v-model="model.returned_by"
                    class="ts-w-full"
                    :options="employees"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                  ></a-select>
                  <div class="validate" v-if="errors.has('returned_by')">
                    {{ errors.first('returned_by') }}
                  </div>
                </div>
                <div>
                  <label class="required">{{
                    $t('stockReturn.location')
                  }}</label>
                  <a-select
                    v-model="model.location_id"
                    class="ts-w-full"
                    :options="warehouses"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                  ></a-select>
                  <div class="validate" v-if="errors.has('location_id')">
                    {{ errors.first('location_id') }}
                  </div>
                </div>
                <div>
                  <label>{{ $t('stockReturn.reference') }}</label>
                  <input
                    v-model="model.reference_no"
                    type="text"
                    class="form-control"
                  />
                  <div class="validate" v-if="errors.has('reference_no')">
                    {{ errors.first('reference_no') }}
                  </div>
                </div>
                <div>
                  <label class="required">{{
                    $t('stockReturn.totalAmount')
                  }}</label>
                  <input
                    v-model="model.total_amount"
                    type="number"
                    class="form-control"
                    readonly
                  />
                  <div class="validate" v-if="errors.has('total_amount')">
                    {{ errors.first('total_amount') }}
                  </div>
                </div>
                <div>
                  <label>{{ $t('stockReturn.remark') }}</label>
                  <textarea
                    v-model="model.remarks"
                    rows="3"
                    class="form-control"
                  ></textarea>
                  <div class="validate" v-if="errors.has('total_amount')">
                    {{ errors.first('total_amount') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-4">
            <div class="col-md-12 ts-space-x-4 ts-flex ts-justify-end">
              <ts-button
                @click.prevent="$router.push({ name: 'stock-return' })"
                >{{ $t('cancel') }}</ts-button
              >
              <ts-button color="primary" @click.prevent="onUpdate">{{
                $t('update')
              }}</ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import moment from 'moment'
import ProductItemDetail from './components/product-item-details.vue'
import { Errors } from 'form-backend-validation'
import { sumBy, isEmpty } from 'lodash'
import { mapActions, mapState } from 'vuex'
export default {
  name: 'stockReturnEdit',
  components: {
    ProductItemDetail
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      customers: [],
      employees: [],
      model: {
        return_id: null,
        location_id: null,
        returned_by: null,
        reference_no: null,
        remarks: null,
        customer_id: null,
        total_amount: 0,
        product_detail: [],
        return_date: moment().format('DD-MM-YYYY')
      }
    }
  },
  computed: {
    ...mapState('stock/stockReturn', ['edit_data']),
    warehouses() {
      return this.$store.state.authUser.user.warehouses.map(el => ({
        value: el.location_id,
        label: el.location_name_en
      }))
    }
  },
  methods: {
    ...mapActions('stock/stockReturn', ['getCustomer', 'getEmployee', 'update', 'find']),
    onUpdate() {
      this.loading = true
      this.errors = new Errors()
      this.update({
        id: this.model.return_id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'stock-return' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setEdit() {
      if(!isEmpty(this.edit_data)){
        this.model.return_id = this.edit_data.return_id
        this.model.location_id = this.edit_data.location_id
        this.model.return_id = this.edit_data.return_id
        this.model.returned_by = this.edit_data.returned_by
        this.model.reference_no = this.edit_data.reference_no
        this.model.remarks = this.edit_data.remarks
        this.model.customer_id = this.edit_data.customer_id
        this.model.total_amount = this.edit_data.total_amount
        this.model.product_detail = this.edit_data.details
        this.model.return_date = this.edit_data.return_date
      }
    },
    calculate() {
      this.model.total_amount = sumBy(this.model.product_detail, o => {
        return o.quantity * o.sale_price
      })
    },
    async fetchResources() {
      this.loading = true
      let responseCustomer = await this.getCustomer()

      this.customers = responseCustomer.data.map(el => ({
        value: el.customer_id,
        label: el.customer_name
      }))

      let responseEmployee = await this.getEmployee()
      this.employees = responseEmployee.data.map(el => ({
        value: el.user_id,
        label: el.name
      }))

      await this.find(this.$route.params.id)

      this.setEdit()

      this.loading = false
    }
  },
  watch: {
    'model.product_detail': {
      deep: true,
      handler() {
        this.calculate()
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResources()
    })
  }
}
</script>
